import React from "react";
import { useTranslation } from "react-i18next";
import { ContentDiv } from "components/containers";
import {
  Title,
  JobSection,
  Members,
  SectionHeader,
  TeamImage,
  TeamSection
} from "./styles";
import { ResponsiveImage } from "../../../components/ResponsiveImage";
import { EE, GR, HR, IT, LT, NL, RU } from "../../../components/Icons/Flags";
import { StyledButton } from "../../../components/generic";
import { useNavigate } from "react-router-dom";
import { VACANCIES_ROUTE } from "../../../utils/constants";
import { OrangePlayFull } from "components/Icons/OrangePlayFull";
import { useIsMobile } from "../../../utils/hooks/useWindowSize";
import { HeaderSEO } from "components/headerSEO";
import { ISeo } from "interfaces/interfaces";

import GroupPhoto from "../../../assets/images/team/new/GroupPhoto.webp";

import Britt from "../../../assets/images/team/new/Britt.webp";
import Daan from "../../../assets/images/team/new/Daan.webp";
import Erki from "../../../assets/images/team/new/Erki.webp";
import Flavia from "../../../assets/images/team/new/Flavia.webp";
import Job from "../../../assets/images/team/new/Job.webp";
import Joost from "../../../assets/images/team/new/Joost.webp";
import Maurits from "../../../assets/images/team/new/Maurits.webp";
import Mykolas from "../../../assets/images/team/new/Mykolas.webp";
import Pavla from "../../../assets/images/team/new/Pavla.webp";
import Vera from "../../../assets/images/team/new/Vera.webp";
import Vincent from "../../../assets/images/team/new/Vincent.webp";
import Thierry from "../../../assets/images/team/thierry-xl.webp";
import Davide from "../../../assets/images/team/davide-xl.webp";

export const Team: React.FC = () => {
  const { t, i18n } = useTranslation("team");
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [textWidth, setTextWidth] = React.useState<string>("175px");

  React.useEffect(() => {
    if (isMobile) {
      setTextWidth("125px");
    } else {
      setTextWidth("175px");
    }
  }, [isMobile]);

  const seo = t("team.seo", { returnObjects: true }) as ISeo;

  const COUNTRIES: { [key: string]: { name: string; icon: JSX.Element } } = {
    NL: {
      name: "The Netherlands",
      icon: NL
    },
    EE: {
      name: "Estonia",
      icon: EE
    },
    LT: {
      name: "Lithuania",
      icon: LT
    },
    RU: {
      name: "Russia",
      icon: RU
    },
    GR: {
      name: "Greece",
      icon: GR
    },
    HR: {
      name: "Croatia",
      icon: HR
    },
    IT: {
      name: "Italy",
      icon: IT
    }
  };

  interface IMember {
    name: string;
    role: string;
    image: string;
    born: string;
    currently: string;
    born_city: string | undefined;
    currently_city: string | undefined;
  }

  const MEMBERS_DICT: IMember[] = [
    {
      name: "Daan Donkers",
      role: "Co-Founder",
      image: Daan,
      born: "NL",
      born_city: "Vught",
      currently: "NL",
      currently_city: "Utrecht"
    },
    {
      name: "Joost Bokkers",
      role: "Co-Founder",
      image: Joost,
      born: "NL",
      born_city: "Vught",
      currently: "NL",
      currently_city: "Amsterdam"
    },
    {
      name: "Maurits Barendregt",
      role: "Co-Founder",
      image: Maurits,
      born: "NL",
      born_city: "Bilthoven",
      currently: "NL",
      currently_city: "Utrecht"
    },
    {
      name: "Davide Iannuzzi",
      role: "Board of Advisors",
      image: Davide,
      born: "IT",
      born_city: undefined,
      currently: "NL",
      currently_city: undefined
    },
    {
      name: "Pavla Shvetcova",
      role: "Digital Marketing Manager",
      image: Pavla,
      born: "RU",
      born_city: undefined,
      currently: "NL",
      currently_city: undefined
    },
    {
      name: "Erki Elbrecht",
      role: "Full-stack Developer",
      image: Erki,
      born: "EE",
      born_city: "Tallinn",
      currently: "NL",
      currently_city: "Leiden"
    },
    {
      name: "Mykolas Metelionis",
      role: "Creative Intern",
      image: Mykolas,
      born: "LT",
      born_city: "",
      currently: "",
      currently_city: ""
    },
    {
      name: "Britt van Heekeren",
      role: "Creative Copywriter",
      image: Britt,
      born: "NL",
      born_city: "",
      currently: "",
      currently_city: ""
    },
    {
      name: "Vera Harsányi-Zorándy",
      role: "Local Promoter Amsterdam",
      image: Vera,
      born: "NL",
      born_city: "",
      currently: "",
      currently_city: ""
    },
    {
      name: "Vincent Stevens",
      role: "Communications & PR",
      image: Vincent,
      born: "NL",
      born_city: "",
      currently: "",
      currently_city: ""
    },
    {
      name: "Flavia Gkioka",
      role: "Local Promoter Groningen",
      image: Flavia,
      born: "GR",
      born_city: undefined,
      currently: "NL",
      currently_city: undefined
    },
    {
      name: "Job Bijlsma",
      role: "Head of Operations",
      image: Job,
      born: "NL",
      born_city: undefined,
      currently: "NL",
      currently_city: undefined
    },
    {
      name: "Thierry van Benten",
      role: "Product Designer",
      image: Thierry,
      born: "NL",
      born_city: "Utrecht",
      currently: "NL",
      currently_city: "Amsterdam"
    }
  ];

  return (
    <ContentDiv>
      {seo && <HeaderSEO {...seo} />}
      <Title>{t("team.title")}</Title>
      <OrangePlayFull
        viewView={textWidth}
        viewHeight="6px"
        marginBottom="0.3em"
        strokeWidth="12"
      />
      <TeamImage src={GroupPhoto} loading={"lazy"} alt={"Group photo"} />
      <TeamSection>
        <SectionHeader>{t("team.teamSection.header")}</SectionHeader>
        <h2>{t("team.teamSection.title")}</h2>
        <p>{t("team.teamSection.description")}</p>
        <Members>
          {MEMBERS_DICT.map((value) => (
            <div>
              <img
                className={"image"}
                src={value.image}
                alt={"Team member image"}
                loading={"lazy"}
              />
              {COUNTRIES[value.born].icon}
              <h1>{value.name}</h1>
              <h2>{value.role}</h2>
            </div>
          ))}
        </Members>
      </TeamSection>
      <JobSection>
        <ResponsiveImage
          className={"image"}
          imgSrc={
            "rootpage/team/Hospita en student aan keukentafel in Groningen"
          }
          mobileSize={"95vw"}
          tabletSize={"40vw"}
          desktopSize={"40vw"}
        />
        <div>
          <SectionHeader>{t("team.jobSection.header")}</SectionHeader>
          <h2>{t("team.jobSection.title")}</h2>
          <p>{t("team.jobSection.description")}</p>
          <StyledButton
            onClick={() =>
              navigate(`/${i18n.language}${VACANCIES_ROUTE.slice(5)}`)
            }
          >
            {t("team.jobSection.button")}
          </StyledButton>
        </div>
      </JobSection>
    </ContentDiv>
  );
};
